/*
 * MathGraph32 Javascript : Software for animating online dynamic mathematics figures
 * http://www.mathgraph32.org/
 * @Author Yves Biton (yves.biton@sesamath.net)
 * @License: GNU AGPLv3 https://www.gnu.org/licenses/agpl-3.0.html
 */
const NatCal = require('../types/NatCal')
const Dimf = require('../types/Dimf')
const kernel = require('../kernel/kernel')
const CValDyn = require('./CValDyn')
const constantes = require('../kernel/constantes')
module.exports = CVariableBornee

/**
 * Classe représentant une variable définie par sa valeur actuelle, ses valeurs mini
 * eet maxi et son pas d'incrémentation.
 * @constructor
 * @extends CValDyn
 * @param {CListeObjets} listeProprietaire : La liste proprétaire de l'objet
 * @param {CImplementationProto} impProto : null ou la construction propriétaire
 * @param {Boolean} estElementFinal : true si l'objet est un élément final de construction
 * @param {String} nomCalcul : Le nom de la variable.
 * @param {Number} valeurActuelle : La valeur actuelle de la variable
 * @param {Number} valeurMini : La valeur mini de la variable
 * @param {Number} valeurMaxi : La valeur maxi de la variable
 * @param {Number} valeurPas : Le pas d'incrémentation
 * @param {String} chaineValeurMini  : Chaîne de caractères représentant le calcul donnant la valeur mini.
 * Ce calcul doit être constant.
 * @param {String} chaineValeurMaxi : Chaîne de caractères représentant le calcul donnant la valeur maxi.
 * Ce calcul doit être constant.
 * @param {String} chaineValeurPas : Chaîne de caractères représentant le calcul donnant le pas.
 * Ce calcul doit être constant.
 * @param {Boolean} dialogueAssocie : true si la figure doit contenir en bas et à droite une boîte
 *  de dialogue comportant des boutons + - et = pour modifier la variable.
 * @returns {CVariableBornee}
 */
function CVariableBornee (listeProprietaire, impProto, estElementFinal, nomCalcul,
    valeurActuelle, valeurMini, valeurMaxi, valeurPas,
    chaineValeurMini, chaineValeurMaxi, chaineValeurPas, dialogueAssocie) {
  if (arguments.length === 1) CValDyn.call(this, listeProprietaire);
  else {
    CValDyn.call(this, listeProprietaire, impProto, estElementFinal);
    this.nomCalcul = nomCalcul;
    this.valeurActuelle = valeurActuelle;
    this.valeurMini = valeurMini;
    this.valeurMaxi = valeurMaxi;
    this.valeurPas = valeurPas;
    this.chaineValeurMini = chaineValeurMini;
    this.chaineValeurMaxi = chaineValeurMaxi;
    this.chaineValeurPas = chaineValeurPas;
    this.dialogueAssocie = dialogueAssocie;
  }
}
CVariableBornee.prototype = new CValDyn();
CVariableBornee.prototype.constructor = CVariableBornee;
CVariableBornee.prototype.superClass = "CValDyn";
CVariableBornee.prototype.className = "CVariableBornee";


CVariableBornee.prototype.getClone = function(listeSource, listeCible) {
  var ind1 = listeSource.indexOf(this.impProto);
  return new CVariableBornee(listeCible, listeCible.get(ind1, "CImplementationProto"),
    this.estElementFinal, this.nomCalcul, this.valeurActuelle, this.valeurMini, this.valeurMaxi, this.valeurPas,
    this.chaineValeurMini, this.chaineValeurMaxi, this.chaineValeurPas, this.dialogueAssocie);
};

CVariableBornee.prototype.setClone = function(ptel) {
  this.valeurActuelle = ptel.valeurActuelle;
  this.valeurMini = ptel.valeurMini;
  this.valeurMaxi = ptel.valeurMaxi;
  this.valeurPas = ptel.valeurPas;
};

CVariableBornee.prototype.getNatureCalcul = function() {
  return NatCal.NVariable;
};

CVariableBornee.prototype.donneNom = function(nouveauNom) {
  this.nom = nouveauNom;
};

CVariableBornee.prototype.rendValeur = function() {
  return this.valeurActuelle;
};
/**
 * Fonction incrémentant la valeur actuelle de la valeur du pas si le résultat
 * est inférieur à la valeur maxi.
 * @returns {undefined}
 */
CVariableBornee.prototype.incremente = function() {
  var nouv = this.valeurActuelle + this.valeurPas;
  if ((nouv >= this.valeurMini) && (nouv <= this.valeurMaxi)) this.valeurActuelle = nouv;
};
/**
 * Fonction décrémentant la valeur actuelle de la valeur du pas si le résultat
 * est supérieur à la valeur mini.
 * @returns {undefined}
 */
CVariableBornee.prototype.decremente = function() {
  var nouv = this.valeurActuelle - this.valeurPas;
  if ((nouv >= this.valeurMini) && (nouv <= this.valeurMaxi))
    this.valeurActuelle = nouv;
};
/**
 * Fonction donnant à la valeur actuelle la valeur valeur, sans vérifier
 * si cette valeur est comprise entre le valeurs mini et maxi.
 * @param {type} valeur
 * @returns {undefined}
 */
CVariableBornee.prototype.donneValeur = function(valeur) {
  this.valeurActuelle = valeur;
};

// A revoir. Utilisé ?
CVariableBornee.prototype.getNom = function() {
  return this.nomCalcul;
};

CVariableBornee.prototype.read = function(inps, list){
  CValDyn.prototype.read.call(this, inps, list);
  this.nomCalcul = inps.readUTF();
  this.valeurActuelle = inps.readDouble();
  this.valeurMini = inps.readDouble();
  this.valeurMaxi = inps.readDouble();
  this.valeurPas = inps.readDouble();
  this.dialogueAssocie = inps.readBoolean();
  this.chaineValeurMini = inps.readUTF();
  this.chaineValeurMaxi = inps.readUTF();
  this.chaineValeurPas = inps.readUTF();
};

CVariableBornee.prototype.write = function(oups, list){
  CValDyn.prototype.write.call(this, oups, list);
  oups.writeUTF(this.nomCalcul);
  oups.writeDouble(this.valeurActuelle);
  oups.writeDouble(this.valeurMini);
  oups.writeDouble(this.valeurMaxi);
  oups.writeDouble(this.valeurPas);
  oups.writeBoolean(this.dialogueAssocie);
  oups.writeUTF(this.chaineValeurMini);
  oups.writeUTF(this.chaineValeurMaxi);
  oups.writeUTF(this.chaineValeurPas);
};
// Fonction créant dans le document un Div associé contenant l'affichage de la valeur de la variable
// et deux boutons + et - pour l'incrémenter et la décrémenter
// ind est l'indice dans la liste des variables associées à des boutons
/**
 * Fonction créant dans le document un Div associé contenant l'affichage de la valeur de la variable
 * et deux boutons + et - pour l'incrémenter et la décrémenter.
 * N'est appelée que si this.dialogueAssocie est true.
 * @param {type} ind : L'indice de cette variable dans la liste des variables ayant un dialue associé
 * en bas et à droite de la figure.
 * @returns {undefined}
 */
CVariableBornee.prototype.creeDiv = function(ind) {
  var x,y,w,h,t,svg,par,style,decx,decy;
  this.div = kernel.ce("div");
  // Il est nécessaire d'utiliser un tableau avec des td car sur tablettes les boutons se retrouvent à la ligne
  var tab = kernel.ce("table");
  this.div.appendChild(tab);
  var tr = kernel.ce("tr");
  tab.appendChild(tr);
  var td = kernel.ce("td", {
    style : "padding:0px"
  });
  tr.appendChild(td);
  this.editeur = kernel.ce("input",{
    readOnly : "true",
    style : "font-Size:9pt;padding:0px;"
  });
  td.appendChild(this.editeur);
  this.editeur.name = this.listeProprietaire.id + this.nomCalcul;
  this.updateDisplay();
  td = kernel.ce("td", {
    style : "padding:0px"
  });
  tr.appendChild(td);
  // Attentino : Utiliser des inputs car si on utilise des buttons il ne faut pas utiliser value mais innerHtml
  this.buttonplus = kernel.ce("input",{
    type : "button",
    value : "+"
  });
  td.appendChild(this.buttonplus);
  t = this;
  this.buttonplus.onclick = function(ev) {
    CVariableBornee.onClickPlus.call(t);
  };
  td = kernel.ce("td", {
    style : "padding:0px"
  });
  tr.appendChild(td);
  this.buttonmoins = kernel.ce("input",{
    type : "button",
    value : "-"
  });
  td.appendChild(this.buttonmoins)
  this.buttonmoins.onclick = function(ev) {
    CVariableBornee.onClickMoins.call(t);
  };
  td = kernel.ce("td", {
    style : "padding:0px"
  });
  tr.appendChild(td);
  this.buttonegal = kernel.ce("input",{
    type : "button",
    value : "="
  });
  td.appendChild(this.buttonegal);
  this.buttonegal.onclick = function() {
    CVariableBornee.onClickEgal.call(t);
  };
  // this.div.appendChild(this.editeur);
  // this.div.appendChild(this.buttonmoins);
  // this.div.appendChild(this.buttonplus);
  // this.div.appendChild(this.buttonegal);
  // Nécessaire d'afficher le div en deux temps pour certaines nvaigateurs'
  svg = document.getElementById(this.listeProprietaire.id);
  par = svg.parentNode; // Le parent du svg doit être un div
  // Modification version mtgApp : Dans le cas d'une mtgApp, le parent du svg de la figure est un autre svg
  // et c'est son div parent qu'il faut utiliser
  if (par.tagName.toLowerCase() !== "div") {
    par = par.parentNode;
    decx = constantes.svgPanelWidth;
    decy = constantes.topIconSize;
  }
  else {
    decx = 0;
    decy = 0;
  }

  // pos = mtg32.getPosition2FirstNonStatic(par);
  // Modifié version 4.9.9.3
  // x = pos.x + parseInt(svg.getAttribute("width"));
  // y = pos.y + parseInt(svg.getAttribute("height"));
  x = parseInt(svg.getAttribute("width")) + decx;
  y = parseInt(svg.getAttribute("height")) + decy;

  // style= "left:"+x+"px;"+"top:"+y+"px;"+"position:absolute;margin:0px;";
  // this.div.setAttribute("style", style);
  // if (par.style.position === "") par.style.position = "relative"; // Modifié version mtgApp
  // Modification version MtgApp pour compatibilité avec les ongelts de Spip
  // Il se peut que le div parent ne soit pas visible et n'ait pas de dimensions.
  // On ajoute d'abord le div au document.body pour connaître sa taille puis on le retire pour l'affecter
  // au div parent de la figure
  style= "left:"+x+"px;"+"top:"+y+"px;"+"position:absolute;margin:0px;";
  this.div.setAttribute("style", style);
  document.body.appendChild(this.div);
  // var bb = document.getElementById(this.listeProprietaire.id).getBoundingClientRect();
  // w = this.editeur.offsetWidth + this.buttonplus.offsetWidth*3;
  // h = Math.max(this.editeur.offsetHeight, this.buttonplus.offsetHeight) + 1;
  w = this.div.offsetWidth;
  h = this.div.offsetHeight;
  x = x - w;
  y = y - h*(ind+1);
  style= "left:"+x+"px;"+"top:"+y+"px;"+"position:absolute;margin:0px;";
  this.div.setAttribute("style", style);
  document.body.removeChild(this.div);
  par.appendChild(this.div);
  // document.body.appendChild(div);
};
/**
 * Fonction appelée quand l'utilisateur clique sur le bouton + du dialoge associé
 * à la variable (si this.dialogueAssocie est true).
 * Augmente la variable de la valeur de son pas.
 * @returns {undefined}
 */
CVariableBornee.onClickPlus = function() {
  var liste,doc,svg,dimf;
  liste = this.listeProprietaire;
  doc = liste.documentProprietaire;
  svg = document.getElementById(liste.id);
  dimf = new Dimf(parseInt(svg.getAttribute("width")), parseInt(svg.getAttribute("height")));
  this.incremente();
  liste.positionneDependantsDe(false, dimf, this);
  liste.setDependantsReady4MathJaxUpdate(this); // Ajout version 5.0.6
  liste.updateDependants(this, svg, doc.couleurFond, doc.opacity, true);
  this.updateDisplay();
};
/**
 * Fonction appelée quand l'utilisateur clique sur le bouton - du dialoge associé
 * à la variable (si this.dialogueAssocie est true).
 * Diminue la variable de la valeur de son pas.
 * @returns {undefined}
 */
CVariableBornee.onClickMoins = function() {
  var liste,doc,svg,dimf;
  liste = this.listeProprietaire;
  doc = liste.documentProprietaire;
  svg = document.getElementById(liste.id);
  dimf = new Dimf(parseInt(svg.getAttribute("width")), parseInt(svg.getAttribute("height")));
  this.decremente();
  liste.positionneDependantsDe(false, dimf, this);
  liste.setDependantsReady4MathJaxUpdate(this); // Ajout version 5.0.6
  liste.updateDependants(this, svg, doc.couleurFond, doc.opacity, true);
  this.updateDisplay();
};
/**
 * Fonction appelée quand l'utilisateur clique sur le bouton = du dialoge associé
 * à la variable (si this.dialogueAssocie est true).
 * Fait apparaître une boîte de dialogue permettant de modifier la valeur actuelle
 * de la variable. La valeur entrée n'est affectée à valeurActuelle que si elle est
 *  comprise entre les valeurs mini et maxi.
 * @returns {undefined}
 */
CVariableBornee.onClickEgal = function() {
  var liste,doc,svg,dimf,va,ch,val;
  ch = kernel.getString("var1")+this.nomCalcul+"\n"+kernel.getString("var2")+this.valeurMini+kernel.getString("et")+this.valeurMaxi;
  va = prompt(ch, this.valeurActuelle);
  if (va === null) return;
  val = parseFloat(va);
  if (!isFinite(val) || (val<this.valeurMini) || (val>this.valeurMaxi)) {
    alert(kernel.getString('incorrect'));
    return;
  }
  this.donneValeur(val);
  liste = this.listeProprietaire;
  doc = liste.documentProprietaire;
  svg = document.getElementById(liste.id);
  dimf = new Dimf(parseInt(svg.getAttribute("width")), parseInt(svg.getAttribute("height")));
  liste.positionneDependantsDe(false, dimf, this);
  liste.setDependantsReady4MathJaxUpdate(this); // Ajout version 5.0.6
  liste.updateDependants(this,svg, doc.couleurFond, doc.opacity, true);
  this.updateDisplay();
};
/**
 * Fonction remettant à jour l'affichage en bas et à droite de la figure du dialogue associé
 * à la variable (ne peut être appelé que si this.dialogueAssocie est true).
 * @returns {undefined}
 */
CVariableBornee.prototype.updateDisplay = function() {
  if (this.dialogueAssocie) this.editeur.value = this.nomCalcul + " = " + this.rendChaineValeur(12);
};
/**
 * Fonction détruisant le div représentant le dialogue affiché en bas et à droite de la figure et associé
 * à la variable, avec les bouton +, - et = associés.
 * Ne peut être appelée que si this.dialogeAssocie est true.
 * @param {type} svg
 * @returns {undefined}
 */
CVariableBornee.prototype.deleteComponent = function(svg) {
  try {
    var par = svg.parentNode;
    // Modification version WepPack. Dans cette version le parent peut être un svg
    if (par.tagName .toLowerCase() !== "div") par = par.parentNode;
    par.removeChild(this.div);
  }
  catch (ex) {}
};



// WEBPACK FOOTER //
// ./src/objets/CVariableBornee.js